/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import { useReportsVentasLayout } from '../reportsVentasLayout';
import { styles } from '../ReportsScreen.styles';
import { ReportsDateRangePicker } from './ReportsDateRangePicker';
import { ReportsSalesListFilterPickers } from './ReportsSalesListFilterPickers';
import { ReportsVentasProductSearchControls } from './ReportsVentasProductSearchControls';
import { ReportsOperatorFilterControl } from './ReportsOperatorFilterControl';
import { ReportsCustomerFilterControl } from './ReportsCustomerFilterControl';
import { ReportsActiveFilterChips } from './ReportsActiveFilterChips';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { OperationalFilterFieldLabel, OperationalFilterSectionDivider } from '../../../shared/filters/OperationalFilterPrimitives'
import { OperationalFiltersCard } from '../../../shared/filters/OperationalFiltersCard'
import { OperationalFiltersPanelShell } from '../../../shared/filters/OperationalFiltersPanelShell'
import { OperationalFiltersClearRow } from '../../../shared/filters/OperationalFiltersClearRow'
import { operationalFilterStyles as opStyles } from '../../../shared/filters/operationalFilters.styles';

type Props = {
  vm: ReportsScreenViewModel;
  disabled?: boolean;
  periodAccessibilityPrefix?: string;
};

/** Alcance compartido Ventas / Personal — card ámbar operativo (mismo lenguaje que Customer). */
const REPORTS_FILTERS_COLLAPSED_STORAGE_KEY = 'reports_scope_filters_collapsed';

export function ReportsScopeFiltersPanel({
  vm,
  disabled = false,
  periodAccessibilityPrefix = 'Período',
}: Props) {
  const ventasLayout = useReportsVentasLayout();
  const { layoutWide } = ventasLayout;
  const busy = disabled || vm.loading || vm.personalLoading;
  // Default colapsado: el panel de filtros es secundario; el usuario lo expande si filtra.
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  const panelActiveFilterCount = useMemo(() => {
    let count = 0;
    if (vm.reportsOperatorFilterId) count += 1;
    if (vm.reportsCustomerFilterId) count += 1;
    if (vm.salesByProductCategory) count += 1;
    if (vm.salesByProductItemTypeFilter !== 'all') count += 1;
    if (vm.reportsVentasBrandFilter) count += 1;
    if (vm.reportsVentasSupplierFilter) count += 1;
    if (vm.reportsCashSessionFilterId) count += 1;
    if (vm.reportsHasDiscountFilter !== 'all') count += 1;
    if (vm.reportsVentasPinnedProductId) count += 1;
    if (vm.reportsPromotionFilterId) count += 1;
    if (vm.reportsPromoCodeFilter) count += 1;
    return count;
  }, [
    vm.reportsOperatorFilterId,
    vm.reportsCustomerFilterId,
    vm.salesByProductCategory,
    vm.salesByProductItemTypeFilter,
    vm.reportsVentasBrandFilter,
    vm.reportsVentasSupplierFilter,
    vm.reportsCashSessionFilterId,
    vm.reportsHasDiscountFilter,
    vm.reportsVentasPinnedProductId,
    vm.reportsPromotionFilterId,
    vm.reportsPromoCodeFilter,
  ]);

  useEffect(() => {
    let alive = true;
    void AsyncStorage.getItem(REPORTS_FILTERS_COLLAPSED_STORAGE_KEY).then((raw) => {
      if (!alive) return;
      setFiltersCollapsed(raw == null ? true : raw === '1');
    });
    return () => {
      alive = false;
    };
  }, []);

  const setFiltersCollapsedPersisted = (next: boolean) => {
    setFiltersCollapsed(next);
    void AsyncStorage.setItem(REPORTS_FILTERS_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
  };

  const toggleFiltersCollapsed = () => {
    setFiltersCollapsedPersisted(!filtersCollapsed);
  };

  const periodBlock = (
    <>
      <OperationalFilterFieldLabel>Período</OperationalFilterFieldLabel>
      <ReportsDateRangePicker
        dateRangePreset={vm.dateRangePreset}
        setDateRangePreset={vm.setDateRangePreset}
        customRangeFrom={vm.customRangeFrom}
        setCustomRangeFrom={vm.setCustomRangeFrom}
        customRangeTo={vm.customRangeTo}
        setCustomRangeTo={vm.setCustomRangeTo}
        disabled={busy}
        presetAccessibilityPrefix={periodAccessibilityPrefix}
      />
      {busy ? (
        <View style={styles.reportsScopeFiltersBusyRow}>
          <ActivityIndicator size="small" color={ticketColors.ink2} />
          <Text style={styles.reportsScopeFiltersBusyText}>Actualizando datos del período…</Text>
        </View>
      ) : null}
    </>
  );

  const itemsFiltersBlock = layoutWide ? (
    <View style={[styles.ventasItemsPeriodBlock, styles.ventasItemsPeriodBlockWide]}>
      <View style={[styles.ventasItemsPeriodFiltersWrap, styles.ventasItemsPeriodFiltersWrapWide]}>
        <View style={[opStyles.wideToolbarRow, styles.reportsSalesListPickerRowWide]}>
          <ReportsSalesListFilterPickers vm={vm} disabled={busy} layout={ventasLayout} embedInGridRow />
          <ReportsOperatorFilterControl vm={vm} disabled={busy} layout={ventasLayout} />
          <ReportsCustomerFilterControl vm={vm} disabled={busy} layout={ventasLayout} />
        </View>
      </View>
      <View style={styles.ventasItemsPeriodSearchWrap}>
        <ReportsVentasProductSearchControls vm={vm} disabled={busy} layout={ventasLayout} />
      </View>
    </View>
  ) : (
    <View style={styles.ventasItemsPeriodBlock}>
      <View style={opStyles.mobilePickerGrid}>
        <ReportsSalesListFilterPickers vm={vm} disabled={busy} layout={ventasLayout} />
        <ReportsOperatorFilterControl vm={vm} disabled={busy} layout={ventasLayout} />
        <ReportsCustomerFilterControl vm={vm} disabled={busy} layout={ventasLayout} />
      </View>
      <ReportsVentasProductSearchControls vm={vm} disabled={busy} layout={ventasLayout} />
    </View>
  );

  const itemsSection = (
    <>
      <OperationalFilterFieldLabel>Ítems del período</OperationalFilterFieldLabel>
      {itemsFiltersBlock}
    </>
  );

  const filtersBody = layoutWide ? (
    <>
      {periodBlock}
      <OperationalFilterSectionDivider />
      {itemsSection}
    </>
  ) : (
    <View style={opStyles.workspaceStack}>
      <View style={opStyles.workspaceSection}>{periodBlock}</View>
      <OperationalFilterSectionDivider />
      <View style={opStyles.workspaceSection}>{itemsSection}</View>
    </View>
  );

  return (
    <View style={styles.reportsScopeFiltersPanel}>
      <OperationalFiltersCard
        bodyStyle={layoutWide ? styles.reportsScopeFiltersCardBody : styles.reportsScopeFiltersCardBodyMobile}
      >
        <OperationalFiltersPanelShell
          title="Alcance"
          isMobile={!layoutWide}
          hint={filtersCollapsed ? vm.dateRangeLabel : undefined}
          hintCompact={filtersCollapsed ? vm.dateRangeLabel : undefined}
          showDividerBeforeBody={!layoutWide}
          bodyStyle={layoutWide ? { gap: spacing.sm, alignSelf: 'stretch' } : undefined}
          collapsible
          collapsed={filtersCollapsed}
          onToggleCollapsed={toggleFiltersCollapsed}
          collapsedActiveFilterCount={panelActiveFilterCount}
        >
          {filtersBody}
          <OperationalFiltersClearRow
            hasActiveFilters={vm.reportsQueryFiltersActive}
            onClearFilters={vm.resetVentasFilters}
          />
        </OperationalFiltersPanelShell>
      </OperationalFiltersCard>
      <ReportsActiveFilterChips vm={vm} />
    </View>
  );
}
